import './GuestAuthDropdown.scss';
import { useEffect, useState } from 'react';
import facebookIcon from './img/facebook-icon.png';
import guestIcon from './img/guest-icon.png';
import googleIcon from './img/google.png';
import appleIcon from './img/apple-icon.png';
import { loginCustomer, verifyCustomer } from '../../services/api/authService';
import { useAuth } from '../../auth/AuthContext';
import { isApiTruthy } from '../../utils/apiValues';
import toast from 'react-hot-toast';

/**
 * Shared login / OTP UI used by header dropdown and checkout modal.
 */
export default function GuestLoginPanel({
  heading = "YOU'RE 1 STEP AWAY!",
  showContinueAsGuest = true,
  onGuestContinue,
  onAuthenticated,
}) {
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState('email');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const { setSession } = useAuth();

  const normalizedEmail = String(email || '').trim().toLowerCase();

  useEffect(() => {
    setError('');
    setInfo('');
    setBusy(false);
    setStep('email');
    setOtp('');
  }, []);

  const sendOtp = async () => {
    setError('');
    setInfo('');
    if (!normalizedEmail) {
      setError('Email is required');
      return;
    }
    setBusy(true);
    try {
      const res = await loginCustomer({ email: normalizedEmail, phone: '' });
      const otpRequired = isApiTruthy(res?.data?.otp_required);
      if (!otpRequired) {
        setInfo(res?.message || 'Success');
        setBusy(false);
        return;
      }
      setStep('otp');
      setInfo('We sent a verification code to your email. Enter it below.');
    } catch (err) {
      setError(err?.message || 'Failed to send OTP');
    } finally {
      setBusy(false);
    }
  };

  const verifyOtp = async () => {
    setError('');
    setInfo('');
    const code = String(otp || '').trim();
    if (!code) {
      setError('OTP is required');
      return;
    }
    setBusy(true);
    try {
      const res = await verifyCustomer({ email: normalizedEmail, otp: code });
      const token = res?.data?.token || '';
      const customer = res?.data?.customer || null;
      setSession(token, customer);
      onAuthenticated?.();
    } catch (err) {
      setError(err?.message || 'Verify failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="guest-login-panel" role="dialog" aria-label="Sign in">
      <div className="guest-auth__title">{heading}</div>

      {showContinueAsGuest ? (
        <>
          <button type="button" className="guest-auth__btn guest-auth__btn--guest" onClick={() => onGuestContinue?.()}>
            <img className="guest-auth__btn-icon" src={guestIcon} alt="" aria-hidden="true" />
            CONTINUE AS GUEST
          </button>

          <div className="guest-auth__divider">
            <span>OR</span>
          </div>
        </>
      ) : null}

      <button
        type="button"
        className="guest-auth__btn guest-auth__btn--facebook"
        onClick={() => toast('Facebook sign-in is not available yet.', { icon: 'ℹ️' })}
      >
        <img className="guest-auth__btn-icon" src={facebookIcon} alt="" aria-hidden="true" />
        SIGN UP FACEBOOK
      </button>
      <button
        type="button"
        className="guest-auth__btn guest-auth__btn--google"
        onClick={() => toast('Google sign-in is not available yet.', { icon: 'ℹ️' })}
      >
        <img className="guest-auth__btn-icon" src={googleIcon} alt="" aria-hidden="true" />
        SIGN UP GOOGLE
      </button>
      <button
        type="button"
        className="guest-auth__btn guest-auth__btn--apple"
        onClick={() => toast('Apple sign-in is not available yet.', { icon: 'ℹ️' })}
      >
        <img className="guest-auth__btn-icon" src={appleIcon} alt="" aria-hidden="true" />
        SIGN UP APPLE ID
      </button>

      <div className="guest-auth__divider">
        <span>OR</span>
      </div>

      <div className="guest-auth__email-label">Enter your email address to log in or sign up</div>

      {info ? (
        <div className="guest-auth__info" role="status">
          {info}
        </div>
      ) : null}
      {error ? (
        <div className="guest-auth__error" role="alert">
          {error}
        </div>
      ) : null}

      <form
        className="guest-auth__email"
        onSubmit={(e) => {
          e.preventDefault();
          if (step === 'email') void sendOtp();
          else void verifyOtp();
        }}
      >
        <input
          className="guest-auth__email-input"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          inputMode="email"
          autoComplete="email"
          disabled={busy || step === 'otp'}
        />

        {step === 'email' ? (
          <button type="submit" className="guest-auth__email-go" aria-label="Send OTP" disabled={busy}>
            {busy ? '…' : '→'}
          </button>
        ) : (
          <button
            type="button"
            className="guest-auth__link"
            onClick={() => {
              setStep('email');
              setOtp('');
              setError('');
              setInfo('');
            }}
            disabled={busy}
          >
            Change
          </button>
        )}
      </form>

      {step === 'otp' ? (
        <form
          className="guest-auth__otp"
          onSubmit={(e) => {
            e.preventDefault();
            void verifyOtp();
          }}
        >
          <input
            className="guest-auth__otp-input"
            placeholder="Enter OTP"
            value={otp}
            onChange={(e) => setOtp(e.target.value)}
            inputMode="numeric"
            autoComplete="one-time-code"
            disabled={busy}
          />
          <button type="submit" className="guest-auth__otp-verify" disabled={busy}>
            {busy ? 'Verifying…' : 'Verify OTP'}
          </button>
        </form>
      ) : null}
    </div>
  );
}
